import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';

import { can } from "../auth/can";
import { PERMS } from "../auth/permissions";
import '../styles/admin-user-ui.css';
import {
  getUsers,
  createUser,
  updateUser,
  DeactivateUser,
  getArmexStations,
  getRoles,
  assignStationsToUser,
  deleteStation,
} from "../api_controll/apiAdmin";
import BackButton from "../components/BackButton";
import { toast } from "react-toastify";

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [stations, setStations] = useState([]);
  const [roles, setRoles] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const [addingUser, setAddingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [addingStationUserId, setAddingStationUserId] = useState(null);
  
  const [statusFilter, setStatusFilter] = useState("active"); 

  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    role_id: "",
    station_id: "",
  });

  const [editedUserData, setEditedUserData] = useState({});
  const [newStationAssignment, setNewStationAssignment] = useState({ userId: "", stationId: "" });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const user = useSelector(state => state.user.user);


  useEffect(() => {
    (async () => {
      try {
        const [usersData, stationData, rolesData] = await Promise.all([
          getUsers(),
          getArmexStations(),
          getRoles(),
        ]);
        setUsers(usersData);
        setStations(stationData);
        setRoles(rolesData);
      } catch (e) {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

    const canDisplayAdmin     = useMemo(() => can(user, PERMS.USER_ADMIN_PANEL), [user]);


  const handleCreateUser = async () => {
    const errors = {};
    if (!newUser.username) errors.username = "Zadej uživatelské jméno.";
    if (!newUser.password) errors.password = "Zadej heslo.";

    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }

    const preparedUser = {
      ...newUser,
      role_id: newUser.role_id || 1,
      station_id: newUser.station_id === "" ? null : Number(newUser.station_id),
    };

    try {
      await createUser(preparedUser);
      setNewUser({ username: "", email: "", phone: "", password: "", role_id: "", station_id: "" });
      setValidationErrors({});
      setAddingUser(false);
      setUsers(await getUsers());
      toast.success("Uživatel uspěšně přidán!");
    } catch (e) {
      toast.error("Nepodařilo se vytvořit uživatele.");
    }
  };

  const handleSaveEdit = async (userId) => {
    try {
      const { password, ...rest } = editedUserData;
      const data = { id: userId, ...rest };
      if (password && password.trim() !== "") data.password = password;

      await updateUser(data);
      setUsers(await getUsers());
      setEditingUserId(null);
      setEditedUserData({});
      toast.success("Údaje změněny!");
    } catch (e) {
      toast.error("Nepodařilo se změnit data.");
    }
  };

    const handleToggleActive = async (user) => {
      const inactive = isInactive(user);
      const next = inactive ;

      if (!window.confirm(`${inactive ? "Aktivovat" : "Deaktivovat"} uživatele ${user.username}?`)) return;

      const payload = { id: user.id, is_active: next };

      try {
        await DeactivateUser(payload);
        setUsers(await getUsers());
        toast.success(inactive ? "Uživatel aktivován." : "Uživatel deaktivován.");
      } catch (e) {
        toast.error("Nepodařilo se změnit stav uživatele.");
      }
    };

  const handleRemoveStation = async (userId, stationId, username, stationName) => {

    if (!window.confirm(`Opravdu odebrat ${stationName} od ${username}?`)) return;
    try {
      const payload = { user_id: userId, station_id: stationId };
      await deleteStation(payload);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, stations: (u.stations || []).filter((s) => s.id !== stationId) } : u
        )
      );
      toast.success("Stanice uspěšně odebrána od uživatele!");
    } catch (e) {
      toast.error("Nepodařilo se odebrat ČS stanici od uživatele.");
    }
  };

  const handleAssignStation = async () => {
    try {
      const payload = {
        user_id: Number(newStationAssignment.userId),
        station_id: Number(newStationAssignment.stationId),
      };
      await assignStationsToUser(payload);
      setUsers(await getUsers());
      toast.success("ČS stanice přidána k uživateli!");
    } catch (e) {
      toast.error("Nepodařilo přidat ČS stanici.");
    }
  };

const isInactive = (user) => !user.is_active;

const filteredUsers = useMemo(() => {
  const term = searchTerm.trim().toLowerCase();

  return users
    .filter((u) => {
      if (statusFilter === "all") return true;
      if (statusFilter === "active") return !isInactive(u);
      return isInactive(u);
    })
    .filter((u) => {
      if (!term) return true;
      return (u.username || "").toLowerCase().includes(term);
    });
}, [users, searchTerm, statusFilter]);

  if (!canDisplayAdmin) return <p className="armx-adminUsers">Nemáš oprávnění pro zobrazení této stránky.</p>;
  if (loading) return <p className="armx-adminUsers">Načítání...</p>;
  if (error) return <p className="armx-adminUsers">{error}</p>;

  return (
    <div className="backB">
    <div className="armx-adminUsers">
      <BackButton />

      <div className="armx-adminUsers__header">
        <div className="armx-adminUsers__titleWrap">
          <h2 className="armx-adminUsers__title">Správa uživatelů</h2>
          <p className="armx-adminUsers__subtitle">
            Vytváření účtů, úprava rolí a správa přiřazených čerpacích stanic.
          </p>
        </div>
      </div>

      <div className="armx-adminUsers__toolbar">
        <div className="armx-adminUsers__search">
          <input
            className="armx-adminUsers__input uzivatel-search"
            placeholder="Hledat uživatele podle jména…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
       <div className="armx-adminUsers__filters">
        <button
          type="button"
          className={`armx-adminUsers__pill ${statusFilter === "active" ? "armx-adminUsers__pill--active" : ""}`}
          onClick={() => setStatusFilter("active")}
        >
          Aktivní
        </button>
        <button
          type="button"
          className={`armx-adminUsers__pill ${statusFilter === "inactive" ? "armx-adminUsers__pill--active" : ""}`}
          onClick={() => setStatusFilter("inactive")}
        >
          Neaktivní
        </button>
        <button
          type="button"
          className={`armx-adminUsers__pill ${statusFilter === "all" ? "armx-adminUsers__pill--active" : ""}`}
          onClick={() => setStatusFilter("all")}
        >
          Všichni
        </button>
      </div>
        <div className="armx-adminUsers__actions">
          {!addingUser ? (
            <button
              type="button"
              className="armx-adminUsers__btn armx-adminUsers__btn--primary"
              onClick={() => setAddingUser(true)}
            >
              + Nový uživatel
            </button>
          ) : (
            <button
              type="button"
              className="armx-adminUsers__btn"
              onClick={() => {
                setAddingUser(false);
                setValidationErrors({});
              }}
            >
              Zavřít formulář
            </button>
          )}
        </div>
      </div>

      {addingUser && (
        <div className="armx-adminUsers__card" style={{ marginTop: 12 }}>
          <div className="armx-adminUsers__panelTitle">Vytvoření uživatele</div>

          <div className="armx-adminUsers__formGrid">
            <div className="armx-adminUsers__field armx-adminUsers__col6">
              <div className="armx-adminUsers__label">Uživatelské jméno</div>
              <input
                className="armx-adminUsers__input"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              />
              {validationErrors.username && <div className="armx-adminUsers__error">{validationErrors.username}</div>}
            </div>

            <div className="armx-adminUsers__field armx-adminUsers__col6">
              <div className="armx-adminUsers__label">Heslo</div>
              <input
                type="password"
                className="armx-adminUsers__input"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
              {validationErrors.password && <div className="armx-adminUsers__error">{validationErrors.password}</div>}
            </div>

            <div className="armx-adminUsers__field armx-adminUsers__col6">
              <div className="armx-adminUsers__label">Email</div>
              <input
                className="armx-adminUsers__input"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>

            <div className="armx-adminUsers__field armx-adminUsers__col6">
              <div className="armx-adminUsers__label">Telefon</div>
              <input
                className="armx-adminUsers__input"
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
              />
            </div>

            <div className="armx-adminUsers__field armx-adminUsers__col6">
              <div className="armx-adminUsers__label">Role</div>
              <select
                className="armx-adminUsers__select"
                value={newUser.role_id}
                onChange={(e) => setNewUser({ ...newUser, role_id: Number(e.target.value) || "" })}
              >
                <option value="">-- Vyber roli --</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <div className="armx-adminUsers__hint">Když nevybereš, použije se defaultně základní role.</div>
            </div>

            <div className="armx-adminUsers__field armx-adminUsers__col6">
              <div className="armx-adminUsers__label">Primární ČS</div>
              <select
                className="armx-adminUsers__select"
                value={newUser.station_id}
                onChange={(e) => setNewUser({ ...newUser, station_id: e.target.value })}
              >
                <option value="">- ČS -</option>
                {stations.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
                            <div className="armx-adminUsers__hint">Může se nechat prázné</div>

            </div>

            <div className="armx-adminUsers__col12" style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" className="armx-adminUsers__btn" onClick={() => setAddingUser(false)}>
                Zrušit
              </button>
              <button type="button" className="armx-adminUsers__btn armx-adminUsers__btn--primary" onClick={handleCreateUser}>
                Vytvořit
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="armx-adminUsers__grid">
        {filteredUsers.map((user) => {
          const assignedIds = (user.stations || []).map((s) => s.id);
          const selectableStations = stations.filter((s) => !assignedIds.includes(s.id));

          const isEditing = editingUserId === user.id;
          const isAssigning = addingStationUserId === user.id;
          
          const inactive = isInactive(user);

          return (
            <div key={user.id} className={`armx-adminUsers__card ${inactive ? "armx-adminUsers__card--inactive" : ""}`}>
              <div className="armx-adminUsers__cardTop">
                <div className="armx-adminUsers__identity">
                  <div className="armx-adminUsers__nameRow">
                    <p className="armx-adminUsers__name">{user.username}</p>
                    <span className="armx-adminUsers__badge">{user.role_name}</span>
                    <span className={`armx-adminUsers__badge ${inactive ? "armx-adminUsers__badge--inactive" : "armx-adminUsers__badge--active"}`}>
                      {inactive ? "Neaktivní" : "Aktivní"}
                    </span>
                  </div>

                  {!isEditing ? (
                    <div className="armx-adminUsers__meta">
                      <span className="armx-adminUsers__metaItem">
                        <span className="armx-adminUsers__metaLabel">Email:</span> {user.email || "—"}
                      </span>
                      <span className="armx-adminUsers__metaItem">
                        <span className="armx-adminUsers__metaLabel">Telefon:</span> {user.phone || "—"}
                      </span>
                    </div>
                  ) : (
                    <div className="armx-adminUsers__panel">
                      <div className="armx-adminUsers__panelTitle">Editace uživatele</div>
                      <div className="armx-adminUsers__formGrid">
                        <div className="armx-adminUsers__field armx-adminUsers__col6">
                          <div className="armx-adminUsers__label">Jméno</div>
                          <input
                            className="armx-adminUsers__input"
                            value={editedUserData.username || ""}
                            onChange={(e) => setEditedUserData({ ...editedUserData, username: e.target.value })}
                          />
                        </div>

                        <div className="armx-adminUsers__field armx-adminUsers__col6">
                          <div className="armx-adminUsers__label">Role</div>
                          <select
                            className="armx-adminUsers__select"
                            value={editedUserData.role_id || ""}
                            onChange={(e) => setEditedUserData({ ...editedUserData, role_id: Number(e.target.value) || "" })}
                          >
                            <option value="">-- Vyber roli --</option>
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="armx-adminUsers__field armx-adminUsers__col6">
                          <div className="armx-adminUsers__label">Email</div>
                          <input
                            className="armx-adminUsers__input"
                            value={editedUserData.email || ""}
                            onChange={(e) => setEditedUserData({ ...editedUserData, email: e.target.value })}
                          />
                        </div>

                        <div className="armx-adminUsers__field armx-adminUsers__col6">
                          <div className="armx-adminUsers__label">Telefon</div>
                          <input
                            className="armx-adminUsers__input"
                            value={editedUserData.phone || ""}
                            onChange={(e) => setEditedUserData({ ...editedUserData, phone: e.target.value })}
                          />
                        </div>

                        <div className="armx-adminUsers__field armx-adminUsers__col12">
                          <div className="armx-adminUsers__label">Nové heslo (nechat prázdné, pokud neměníš)</div>
                          <input
                            type="password"
                            className="armx-adminUsers__input"
                            value={editedUserData.password || ""}
                            onChange={(e) => setEditedUserData({ ...editedUserData, password: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="armx-adminUsers__cardBtns">
                  {isEditing ? (
                    <>
                      <button type="button" className="armx-adminUsers__btn armx-adminUsers__btn--primary" onClick={() => handleSaveEdit(user.id)}>
                        Uložit
                      </button>
                      <button
                        type="button"
                        className="armx-adminUsers__btn"
                        onClick={() => {
                          setEditingUserId(null);
                          setEditedUserData({});
                        }}
                      >
                        Zrušit
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="armx-adminUsers__btn" onClick={() => setAddingStationUserId(isAssigning ? null : user.id)}>
                        Stanice
                      </button>
                      <button
                        type="button"
                        className="armx-adminUsers__btn armx-adminUsers__btn--primary"
                        onClick={() => {
                          setEditingUserId(user.id);
                          setEditedUserData({
                            username: user.username,
                            email: user.email,
                            phone: user.phone || "",
                            role_id: user.role_id,
                            password: "",
                          });
                        }}
                      >
                        Upravit
                      </button>
                     <button
                      type="button"
                      className={`armx-adminUsers__btn ${inactive ? "armx-adminUsers__btn--primary" : "armx-adminUsers__btn--danger"}`}
                      onClick={() => handleToggleActive(user)}
                    >
                      {inactive ? "Aktivovat" : "Deaktivovat"}
                    </button>
                    </>
                  )}
                </div>
              </div>

              {!!(user.stations && user.stations.length) && (
                <div className="armx-adminUsers__chips" aria-label="Přiřazené čerpací stanice">
                  {user.stations.map((s) => (
                    <span key={s.id} className="armx-adminUsers__chip">
                      {s.name}
                      <button
                        type="button"
                        className="armx-adminUsers__chipX"
                        title="Odebrat stanici"
                        onClick={() => handleRemoveStation(user.id, s.id, user.username, s.name)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {isAssigning && (
                <div className="armx-adminUsers__panel">
                  <div className="armx-adminUsers__panelTitle">Přiřadit ČS uživateli</div>

                  <div className="armx-adminUsers__formGrid">
                    <div className="armx-adminUsers__field armx-adminUsers__col6">
                      <div className="armx-adminUsers__label">Uživatel</div>
                      <input className="armx-adminUsers__input" readOnly value={user.username} />
                    </div>

                    <div className="armx-adminUsers__field armx-adminUsers__col6">
                      <div className="armx-adminUsers__label">Čerpací stanice</div>
                      <select
                        className="armx-adminUsers__select"
                        disabled={selectableStations.length === 0}
                        onChange={(e) => setNewStationAssignment({ userId: user.id, stationId: e.target.value })}
                      >
                        {selectableStations.length === 0 ? (
                          <option value="">-- Všechny stanice přiřazené --</option>
                        ) : (
                          <>
                            <option value="">-- Vyber ČS --</option>
                            {selectableStations.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="armx-adminUsers__col12" style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                      <button type="button" className="armx-adminUsers__btn" onClick={() => setAddingStationUserId(null)}>
                        Zavřít
                      </button>
                      <button
                        type="button"
                        className="armx-adminUsers__btn armx-adminUsers__btn--primary"
                        onClick={handleAssignStation}
                        disabled={!newStationAssignment.stationId}
                      >
                        Přidat
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
}
